import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../../components/reusable/Seo/Seo.js';
import PageHero from '../../../components/ui/PageHero.js';
import Reveal from '../../../components/ui/Reveal.js';
import {
  organizationSchema,
  breadcrumbSchema,
  physicianSchema,
} from '../../../lib/schema.js';
import { leadership, physicians, advancedPractice } from '../../../content/team.js';
import './Team.scss';

function Team() {
  // Only the founder gets the full profile treatment; everyone else -
  // including operations, renders at the same card size (Jake's request).
  const founder = leadership.find((m) => m.isFounder);
  const profileMembers = founder ? [founder] : [];
  const operations = leadership.filter((m) => !m.isFounder);

  const jsonLd = [
    organizationSchema(),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Leadership & Team', path: '/team' },
    ]),
  ];
  if (founder) {
    jsonLd.push(physicianSchema({ ...founder, message: founder.bio[0] }));
  }

  return (
    <div className="team-page">
      <Seo
        title="Leadership & Team"
        description="Meet the leadership of Virginia Rehab Physicians, a physician-owned PM&R group led by its founder and medical director, supported by board-certified physiatrists and advanced practice providers across Virginia."
        path="/team"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Our team"
        title={
          <>
            Led by the physicians <em>who built it.</em>
          </>
        }
        lede="Physician-owned means exactly that: the people running Virginia Rehab Physicians are the same people rounding on its units. There is no distant management layer. Clinical judgment sits at the top of the org chart."
        secondary={{ label: 'Join the team', href: '/careers' }}
        breadcrumb={[{ label: 'Home', path: '/' }, { label: 'Leadership & Team' }]}
      />

      {/* ---- Leadership profiles ---- */}
      <section className="team-leadership pg-section" aria-labelledby="leadership-heading">
        <div className="u-container">
          <Reveal className="pg-head">
            <span className="u-eyebrow">Leadership</span>
            <h2 id="leadership-heading">Accountable, by name.</h2>
            <p className="pg-lede">
              Responsible for what Virginia Rehab Physicians promises its
              partners, and for keeping that promise on the unit.
            </p>
          </Reveal>

          <div className="team-leadership__list">
            {profileMembers.map((member, i) => (
              <Reveal
                as="article"
                className={`team-profile ${i % 2 === 1 ? 'team-profile--flip' : ''}`}
                key={member.slug}
                delay={0.05}
              >
                <figure className="team-profile__media">
                  <img
                    src={member.image}
                    alt={`Portrait of ${member.name}, ${member.designation} of Virginia Rehab Physicians`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </figure>
                <div className="team-profile__body">
                  {member.isFounder && <span className="team-profile__flag">Founder</span>}
                  <h3 className="team-profile__name">{member.name}</h3>
                  <p className="team-profile__role">{member.designation}</p>
                  <div className="team-profile__bio">
                    {member.bio.map((para) => (
                      <p className="team-profile__para" key={para.slice(0, 32)}>
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---- The clinical team ---- */}
      <section className="team-wider pg-section pg-section--alt" aria-labelledby="wider-heading">
        <div className="u-container">
          <Reveal className="pg-head">
            <span className="u-eyebrow">The clinical team</span>
            <h2 id="wider-heading">Selective by design.</h2>
            <p className="pg-lede">
              Board-certified physiatrists and advanced practice providers
              cover our units across Virginia. Each is here because
              they practice rehabilitation medicine the way we believe it
              should be practiced: present, consistent, and accountable to
              outcomes.
            </p>
          </Reveal>

          <div className="team-roster">
            {[
              { label: 'Physicians', members: physicians },
              { label: 'Advanced practice providers', members: advancedPractice },
              { label: 'Operations', members: operations },
            ].map((group) => (
              <div className="team-roster__group" key={group.label}>
                <Reveal as="h3" className="team-roster__label">
                  {group.label}
                </Reveal>
                <ul className="team-roster__grid">
                  {group.members.map((m, i) => (
                    <Reveal as="li" className="team-card" key={m.slug} delay={0.04 * i}>
                      <figure className="team-card__media">
                        <img
                          src={m.image}
                          alt={`Portrait of ${m.name}, ${m.designation} at Virginia Rehab Physicians`}
                          loading="lazy"
                        />
                      </figure>
                      <h4 className="team-card__name">{m.name}</h4>
                      <p className="team-card__role">{m.designation}</p>
                    </Reveal>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Closing CTA ---- */}
      <section className="team-cta u-grain" aria-labelledby="team-cta-heading">
        <div className="u-container team-cta__inner">
          <Reveal>
            <h2 id="team-cta-heading" className="team-cta__title">
              Think you might belong on <em>this page?</em>
            </h2>
            <p className="team-cta__lede">
              We hire deliberately. We look for physiatrists and advanced
              practice providers who want their name to stand for something.
            </p>
            <Link to="/careers" className="btn btn--gold">
              Explore careers{' '}
              <span className="btn__arrow" aria-hidden="true">
                &rarr;
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Team;
